import { confirmEmailChange, requestEmailChange } from "api/auth";
import { fieldLimits } from "config";
import React, { type ChangeEvent, type FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";

import type { ChangeEmailDialogProps } from "../settings.types";

import styles from "./ChangeEmailDialog.module.css";

type Step = "email" | "otp";

/** Seconds the user must wait between code sends. */
const RESEND_COOLDOWN_SECONDS = 30;

const ChangeEmailDialog = ({ currentEmail, onClose, onChanged }: ChangeEmailDialogProps) => {
	const { t } = useTranslation("settingsPage");
	const { getAccessToken } = useSession();

	const [step, setStep] = useState<Step>("email");
	const [newEmail, setNewEmail] = useState("");
	const [otpCode, setOtpCode] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isResending, setIsResending] = useState(false);
	const [resendCooldown, setResendCooldown] = useState(0);
	const [resent, setResent] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	// Tick the resend cooldown down to zero, one second at a time.
	useEffect(() => {
		if (resendCooldown <= 0) {
			return undefined;
		}

		const timer = window.setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);

		return () => window.clearTimeout(timer);
	}, [resendCooldown]);

	const onNewEmailChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => setNewEmail(event.target.value),
		[]
	);
	const onOtpChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => setOtpCode(event.target.value), []);

	const onRequest = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();
			if (isSubmitting) {
				return;
			}

			const trimmed = newEmail.trim();
			if (!trimmed) {
				setErrorMessage(t("changeEmail.emailRequired"));

				return;
			}

			setErrorMessage("");
			setIsSubmitting(true);
			try {
				const token = await getAccessToken();
				if (!token) {
					return;
				}

				await requestEmailChange(trimmed, token);
				setStep("otp");
				setResendCooldown(RESEND_COOLDOWN_SECONDS);
			} catch (error) {
				setErrorMessage(error instanceof Error ? error.message : t("changeEmail.genericError"));
			} finally {
				setIsSubmitting(false);
			}
		},
		[getAccessToken, isSubmitting, newEmail, t]
	);

	const onResend = useCallback(async (): Promise<void> => {
		if (isResending || resendCooldown > 0) {
			return;
		}

		setErrorMessage("");
		setResent(false);
		setIsResending(true);
		try {
			const token = await getAccessToken();
			if (!token) {
				return;
			}

			await requestEmailChange(newEmail.trim(), token);
			setOtpCode("");
			setResent(true);
			setResendCooldown(RESEND_COOLDOWN_SECONDS);
		} catch (error) {
			setErrorMessage(error instanceof Error ? error.message : t("changeEmail.genericError"));
		} finally {
			setIsResending(false);
		}
	}, [getAccessToken, isResending, newEmail, resendCooldown, t]);

	const onConfirm = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();
			if (isSubmitting) {
				return;
			}

			const trimmed = otpCode.trim();
			if (!trimmed) {
				setErrorMessage(t("changeEmail.otpRequired"));

				return;
			}

			setErrorMessage("");
			setIsSubmitting(true);
			try {
				const token = await getAccessToken();
				if (!token) {
					return;
				}

				const account = await confirmEmailChange(trimmed, token);
				onChanged(account);
			} catch (error) {
				setErrorMessage(error instanceof Error ? error.message : t("changeEmail.genericError"));
			} finally {
				setIsSubmitting(false);
			}
		},
		[getAccessToken, isSubmitting, onChanged, otpCode, t]
	);

	return (
		<div className={styles.overlay}>
			{step === "email" ? (
				<form className={styles.dialog} onSubmit={onRequest}>
					<header className={styles.header}>
						<h2>{t("changeEmail.title")}</h2>
						<p>{t("changeEmail.emailDescription", { email: currentEmail })}</p>
					</header>

					<label className={styles.field} htmlFor="change-email-new">
						<span className={styles.fieldLabel}>{t("changeEmail.newEmail")}</span>
						<input
							autoComplete="email"
							className={styles.input}
							disabled={isSubmitting}
							id="change-email-new"
							maxLength={fieldLimits.email}
							onChange={onNewEmailChange}
							type="email"
							value={newEmail}
						/>
					</label>

					{errorMessage && <p className={styles.error}>{errorMessage}</p>}

					<footer className={styles.footer}>
						<button className={styles.cancelButton} disabled={isSubmitting} onClick={onClose} type="button">
							{t("changeEmail.cancel")}
						</button>
						<button className={styles.primaryButton} disabled={isSubmitting} type="submit">
							{isSubmitting ? t("changeEmail.sending") : t("changeEmail.sendCode")}
						</button>
					</footer>
				</form>
			) : (
				<form className={styles.dialog} onSubmit={onConfirm}>
					<header className={styles.header}>
						<h2>{t("changeEmail.title")}</h2>
						<p>{t("changeEmail.otpDescription", { email: newEmail.trim() })}</p>
					</header>

					<label className={styles.field} htmlFor="change-email-otp">
						<span className={styles.fieldLabel}>{t("changeEmail.code")}</span>
						<input
							autoComplete="one-time-code"
							className={`${styles.input} ${styles.otpInput}`}
							disabled={isSubmitting}
							id="change-email-otp"
							inputMode="numeric"
							maxLength={fieldLimits.otpCode}
							onChange={onOtpChange}
							type="text"
							value={otpCode}
						/>
					</label>

					{resent && <p className={styles.resentNote}>{t("changeEmail.resent")}</p>}
					{errorMessage && <p className={styles.error}>{errorMessage}</p>}

					<div className={styles.resendRow}>
						<span>{t("changeEmail.noCode")}</span>
						<button
							className={styles.linkButton}
							disabled={isResending || resendCooldown > 0}
							onClick={onResend}
							type="button">
							{resendCooldown > 0
								? t("changeEmail.resendIn", { seconds: resendCooldown })
								: t("changeEmail.resend")}
						</button>
					</div>

					<footer className={styles.footer}>
						<button
							className={styles.cancelButton}
							disabled={isSubmitting || isResending}
							onClick={onClose}
							type="button">
							{t("changeEmail.cancel")}
						</button>
						<button className={styles.primaryButton} disabled={isSubmitting || isResending} type="submit">
							{isSubmitting ? t("changeEmail.verifying") : t("changeEmail.verify")}
						</button>
					</footer>
				</form>
			)}
		</div>
	);
};

export default ChangeEmailDialog;
